"use client";

import * as React from "react";
import { Check, ArrowRight, ShoppingBag, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton, ProductPrice } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
const wp_query_params = {
  per_page: 4,
  order: "desc",
  orderby: "date"
};
const wp_query = new WP_Query(wp_query_params);
function StoreGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({
        length: 4
      }).map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4">
            <Skeleton className="aspect-square w-full rounded-2xl" />
            <Skeleton className="h-6 w-3/4 rounded-md" />
            <Skeleton className="h-5 w-1/3 rounded-md" />
            <Skeleton className="h-10 w-full rounded-full" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-2xl border border-border bg-card p-10 text-center text-card-foreground">
        <p className="text-sm font-medium text-muted-foreground">
          No se pudieron cargar los productos destacados en este momento.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-4 rounded-full">
          Reintentar carga
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-2xl border border-border bg-card p-12 text-center text-card-foreground">
        <ShoppingBag className="size-10 text-muted-foreground" />
        <h3 className="mt-3 text-lg font-semibold font-default">Catálogo en actualización</h3>
        <p className="mt-1 max-w-md text-sm text-muted-foreground">
          Estamos renovando nuestro inventario de repuestos y equipamiento profesional.
        </p>
        <Button asChild variant="default" className="mt-6 rounded-full bg-primary text-primary-foreground">
          <Link to="/ShopPage">Visitar tienda</Link>
        </Button>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {products.map((product, i) => <Card key={product.id} data-index={i} className="group relative flex flex-col justify-between overflow-hidden rounded-2xl border border-border bg-card p-0 shadow-sm transition-all duration-300 hover:shadow-md">
          <div className="relative aspect-square w-full overflow-hidden bg-muted/40">
            {product.on_sale && <div className="absolute top-3 left-3 z-10">
                <Badge variant="default" className="rounded-full bg-chart-1 px-2.5 py-0.5 text-xs font-semibold text-primary-foreground">
                  Oferta
                </Badge>
              </div>}
            <Link to={product.link} className="block size-full" aria-label={product.title}>
              {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" className="size-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="flex size-full items-center justify-center text-muted-foreground">
                  <ShoppingBag className="size-12 stroke-[1.25]" />
                </div>}
            </Link>
          </div>

          <CardHeader className="gap-2 p-5 pb-2">
            {product.categories && product.categories.length > 0 && <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {product.categories[0].name}
              </span>}
            <CardTitle className="line-clamp-2 text-base font-semibold leading-snug font-default">
              <Link to={product.link} className="text-foreground transition-colors hover:text-chart-1">
                {product.title}
              </Link>
            </CardTitle>
          </CardHeader>

          <CardContent className="px-5 py-2">
            <ProductPrice product={product} className="text-lg font-bold font-default text-foreground" />
          </CardContent>

          <CardFooter className="p-5 pt-3">
            <AddToCartButton product={product} productId={product.id} className="w-full" buttonClassName="w-full rounded-full bg-primary text-primary-foreground font-medium transition-all hover:opacity-95" text="Añadir al carrito" showQuantity={false} />
          </CardFooter>
        </Card>)}
    </div>;
}
export default function Store() {
  return <section id="featuredstore" data-nav="light" className="w-full bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto mb-14 max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            <Check className="size-3.5 text-chart-1 stroke-[2.5]" />
            <span>Nuestra Tienda</span>
            <Check className="size-3.5 text-chart-1 stroke-[2.5]" />
          </div>

          <h2 className="mt-4 font-mono text-3xl font-bold uppercase tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Materiales y Equipos Destacados
          </h2>

          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            Suministros certificados de fontanería, climatización y mantenimiento de piscinas con garantía directa y envío rápido a Totana, Murcia y comarcas limítrofes.
          </p>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-6 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5 font-medium">
              <ShieldCheck className="size-4 text-chart-1" />
              Garantía profesional oficial
            </span>
            <span className="inline-flex items-center gap-1.5 font-medium">
              <Check className="size-4 text-chart-1" />
              Stock para entrega inmediata
            </span>
          </div>
        </div>

        {/* Dynamic Products Grid */}
        <EcommerceProductsProvider wp_query={{
        per_page: 4,
        order: "desc",
        orderby: "date"
      }} className="w-full">
          <StoreGridConsumer />
        </EcommerceProductsProvider>

        {/* Bottom CTA Pill */}
        <div className="mt-14 flex justify-center">
          <Button asChild size="lg" className="rounded-full bg-primary px-8 py-6 text-base font-semibold text-primary-foreground shadow-md transition-all hover:bg-primary/90">
            <Link to="/ShopPage" className="inline-flex items-center gap-2">
              <span>Ver tienda completa</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}